// ═══════════════════════════════════════════════════════════════════════════
// AI panel transcript model (CC-2d)
// ═══════════════════════════════════════════════════════════════════════════
//
// Session-scoped store shared by the drafting + support panels. Lives in a
// dedicated zustand store (NOT React component state) so the transcript —
// the ТЗ §13 "transcript log" obligation — survives panel tab switches and
// inspector collapse/expand within the session. Display-only: entries are
// resolved localized strings captured at action time (chat-log semantics);
// the document/audit state of an AI turn is owned by the safe contour +
// editor store, never by this model.

import { create } from "zustand";

/** Drafting-panel transcript entry kinds (review-first outcomes). */
export type AiDraftEntryKind = "accepted" | "rejected" | "failed";

export interface AiDraftTranscriptEntry {
  id: string;
  kind: AiDraftEntryKind;
  /** Short localized headline (Accepted / Rejected / Execution failed). */
  title: string;
  /** Localized detail: actions, command count, undo label or reason. */
  detail: string;
  /** Visual tone — maps onto the shared `history-card tone-*` styles. */
  tone: "success" | "warning" | "info";
}

export interface AiSupportTranscriptEntry {
  id: string;
  /** The user's question, verbatim. */
  question: string;
  /** Localized answer paragraphs from the support provider. */
  paragraphs: string[];
}

/**
 * Ring cap — matches the shell transcript / audit ring bound (48) so a
 * long session cannot grow the panel without limit.
 */
const MAX_TRANSCRIPT_ENTRIES = 48;

let nextEntryId = 1;

function mintEntryId(prefix: string): string {
  return `${prefix}-${nextEntryId++}`;
}

interface AiPanelTranscriptStore {
  /** Drafting input draft — survives tab switches like the transcript. */
  draftInput: string;
  /** Latest-first drafting outcomes (accept / reject / failure). */
  draftEntries: AiDraftTranscriptEntry[];
  /** Latest-first support Q&A entries. */
  supportEntries: AiSupportTranscriptEntry[];
  setDraftInput: (input: string) => void;
  appendDraftEntry: (entry: Omit<AiDraftTranscriptEntry, "id">) => void;
  appendSupportEntry: (entry: Omit<AiSupportTranscriptEntry, "id">) => void;
  /** Clears the whole session log (tests / explicit session reset). */
  reset: () => void;
}

export const useAiPanelTranscriptStore = create<AiPanelTranscriptStore>(
  (set) => ({
    draftInput: "",
    draftEntries: [],
    supportEntries: [],
    setDraftInput: (input) => set({ draftInput: input }),
    appendDraftEntry: (entry) =>
      set((store) => ({
        draftEntries: [
          { ...entry, id: mintEntryId("ai-draft") },
          ...store.draftEntries,
        ].slice(0, MAX_TRANSCRIPT_ENTRIES),
      })),
    appendSupportEntry: (entry) =>
      set((store) => ({
        supportEntries: [
          { ...entry, id: mintEntryId("ai-support") },
          ...store.supportEntries,
        ].slice(0, MAX_TRANSCRIPT_ENTRIES),
      })),
    reset: () =>
      set({ draftInput: "", draftEntries: [], supportEntries: [] }),
  }),
);

/** Test helper — reset the session transcript between specs. */
export function resetAiPanelTranscript(): void {
  useAiPanelTranscriptStore.getState().reset();
}
